import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaQuoteLeft } from 'react-icons/fa';
import './WhyUsPage.css';

const comparisonData = [
  { nafmedprep: "Reviewed directly by a resident physician with admissions experience", competitor: "Rotating reviewer pool" },
  { nafmedprep: "Same reviewer throughout", competitor: "Multiple reviewers" },
  { nafmedprep: "Recent admissions-cycle insight", competitor: "Older playbooks & templates" },
  { nafmedprep: "1-on-1 personalized feedback", competitor: "Template-based edits" },
  { nafmedprep: "Direct access to your reviewer", competitor: "Limited reviewer contact" },
  { nafmedprep: "Detailed line-by-line edits", competitor: "Surface-level feedback" },
  { nafmedprep: "Fast turnaround options", competitor: "Queue-based turnaround" },
  { nafmedprep: "Student-friendly pricing", competitor: "Premium corporate pricing" },
];

const studentQuotes = [
  "I tried a big-name advising service before this and the feedback felt really generic. With NafMedPrep, every edit and comment was specific to my story and goals. It felt way more one-on-one instead of corporate.",
  "What I liked most was working with the same person every time instead of getting passed around between reviewers. The feedback built on itself and actually got more targeted each round. That made a huge difference.",
  "Getting advice from someone who just went through the med school application process recently was honestly huge. The tips were current and realistic, not outdated. You can tell Nafis bases it on real experience.",
  "I looked into some of the big advising companies and the prices were honestly overwhelming. This felt much more affordable but still extremely high quality. I am so grateful I found NafMedPrep.",
];

const WhyUsPage: React.FC = () => {
  return (
    <div className="why-us-page">
      <section className="why-us-hero">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Why Students Choose NafMedPrep</h1>
            <p className="why-us-subtitle">
              Personal mentorship. Recent admissions insight. Research credibility. No corporate advising structure.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="comparison-section">
        <div className="container">
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comparison-cell header-nafmedprep">NafMedPrep</div>
              <div className="comparison-cell header-competitor">Large Advising Companies</div>
            </div>
            {comparisonData.map((row, index) => (
              <motion.div
                key={index}
                className="comparison-row"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <div className="comparison-cell nafmedprep-cell">
                  <FaCheckCircle className="check-icon" />
                  <span>{row.nafmedprep}</span>
                </div>
                <div className="comparison-cell competitor-cell">
                  <FaTimesCircle className="times-icon" />
                  <span>{row.competitor}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="student-quotes-section">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            What Students Are Saying
          </motion.h2>
          <div className="quotes-grid">
            {studentQuotes.map((quote, index) => (
              <motion.div
                key={index}
                className="quote-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <FaQuoteLeft className="quote-icon" />
                <p>{quote}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="why-us-cta">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2>Ready to get started?</h2>
            <p>See the difference that personalized, expert mentorship can make.</p>
            <Link to="/contact" className="btn btn-primary">Book a Free Consultation</Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhyUsPage;
